<template>
  <div>
    <h2>{{ $t('views.endusers.tenants.title') }}</h2>

    <b-table
      :busy="isLoading"
      :fields="tableFields"
      :items="mappedTenants"
      striped
      hover
      show-empty
      sort-icon-left
    >
      <template #table-busy>
        <div class="text-center">
          <loader type="dots" inline scale="4" />
          <div>{{ $t('components.table.busy') }}</div>
        </div>
      </template>

      <template #cell(tenantName)="data">
        {{ data.value }}
      </template>

      <template #cell(created)="data">
        <span v-if="data.value" v-b-tooltip.hover.left="$d(new Date(data.value), 'long')">
          <timeago :datetime="data.value" :converterOptions="{ format: 'Pp' }" auto-update />
        </span>
        <span v-else>-</span>
      </template>

      <template #cell(lastPasswordReset)="data">
        <span v-if="data.value" v-b-tooltip.hover.left="$d(new Date(data.value), 'long')">
          <timeago :datetime="data.value" :converterOptions="{ format: 'Pp' }" auto-update />
        </span>
        <span v-else>-</span>
      </template>

      <template #cell(updated)="data">
        <span v-if="data.value" v-b-tooltip.hover.left="$d(new Date(data.value), 'long')">
          <timeago :datetime="data.value" :converterOptions="{ format: 'Pp' }" auto-update />
        </span>
        <span v-else>-</span>
      </template>

      <template #cell(hasTenantLogin)="data">
        <b-badge v-if="data.value" variant="success">{{ $t('views.endusers.tenants.table.value.hasTenantLoginYes') }}</b-badge>
        <b-badge v-else variant="secondary">{{ $t('views.endusers.tenants.table.value.hasTenantLoginNo') }}</b-badge>
      </template>

      <template #cell(emailConfirmed)="data">
        <span v-if="!data.item.hasTenantLogin">-</span>
        <b-badge v-else-if="data.value" variant="success">{{ $t('views.endusers.tenants.table.value.emailConfirmedYes') }}</b-badge>
        <b-badge v-else variant="secondary">{{ $t('views.endusers.tenants.table.value.emailConfirmedNo') }}</b-badge>
      </template>

      <template #cell(lockoutEndDate)="data">
        <span v-if="data.value && new Date(data.value) > new Date()" v-b-tooltip.hover.left="$d(new Date(data.value), 'long')">
          <timeago :datetime="data.value" :converterOptions="{ format: 'Pp' }" auto-update />
        </span>
        <span v-else>-</span>
      </template>

      <template #cell(actions)="data">
        <div v-if="data.item.hasTenantLogin && data.item.endUserTenantLoginId" class="d-flex actions-buttons">
          <b-button
            size="sm"
            variant="warning"
            class="mr-1"
            :disabled="isActionLoading(data.item.endUserTenantLoginId, 'triggerPasswordreset')"
            @click="triggerPasswordreset(data.item.endUserId, data.item)"
          >
            <b-icon icon="reset" />
            {{ $t('views.endusers.tenants.action.resetpassword') }}
          </b-button>
          <b-button
            v-if="!data.item.emailConfirmed"
            size="sm"
            variant="warning"
            class="mr-1"
            :disabled="isActionLoading(data.item.endUserTenantLoginId, 'triggerEmailConfirmation')"
            @click="triggerEmailConfirmation(data.item.endUserId, data.item)"
          >
            <b-icon icon="check" />
            {{ $t('views.endusers.tenants.action.setemailconfirmed') }}
          </b-button>
          <b-button
            v-if="data.item.lockoutEndDate && new Date(data.item.lockoutEndDate) > new Date()"
            size="sm"
            variant="warning"
            class="mr-1"
            :disabled="isActionLoading(data.item.endUserTenantLoginId, 'triggerResetLockout')"
            @click="triggerResetLockout(data.item.endUserId, data.item)"
          >
            <b-icon icon="reset" />
            {{ $t('views.endusers.tenants.action.resetlockout') }}
          </b-button>
        </div>
      </template>

      <template #empty>
        <div class="text-center">{{ $t('components.table.empty') }}</div>
      </template>
    </b-table>
  </div>
</template>

<script>
import Loader from '@/components/Loader';
import MixinEntityBase from '@/components/mixins/EntityBase';
import MixinEndUserSections from '@/views/EndUsers/_mixins/endUserSections';

export default {
  name: 'EndUsers.Tenants',
  mixins: [MixinEntityBase, MixinEndUserSections],
  components: {
    Loader
  },
  data() {
    return {
      tableFields: [
        {
          key: 'tenantName',
          label: this.$t('views.endusers.tenants.table.label.tenantName'),
          sortable: true
        },
        {
          key: 'hasTenantLogin',
          label: this.$t('views.endusers.tenants.table.label.hasTenantLogin'),
          sortable: true
        },
        {
          key: 'emailConfirmed',
          label: this.$t('views.endusers.tenants.table.label.emailConfirmed'),
          sortable: true
        },
        {
          key: 'lockoutEndDate',
          label: this.$t('views.endusers.tenants.table.label.lockoutEndDate'),
          sortable: true,
          class: 'cell-align-right'
        },
        {
          key: 'lastPasswordReset',
          label: this.$t('views.endusers.tenants.table.label.lastPasswordReset'),
          sortable: true,
          class: 'cell-align-right'
        },
        {
          key: 'created',
          label: this.$t('views.endusers.tenants.table.label.created'),
          sortable: true,
          class: 'cell-align-right'
        },
        {
          key: 'updated',
          label: this.$t('views.endusers.tenants.table.label.updated'),
          sortable: true,
          class: 'cell-align-right'
        },
        {
          key: 'actions',
          label: this.$t('views.endusers.tenants.table.label.actions'),
          sortable: false,
          class: 'cell-align-center actions-column',
          thStyle: { whiteSpace: 'nowrap' },
          tdClass: 'actions-cell'
        }
      ]
    }
  },
  computed: {
    tenantsIs() {
      return this.$store.getters['tenants/is']
    },
    tenantLoginsIs() {
      return this.$store.getters['endusertenantlogins/is']
    },
    isLoading() {
      return this.tenantsIs.loading || this.tenantLoginsIs.loading
    },
    tenants() {
      return this.$store.getters['tenants/getUnwrapped'] || []
    },
    mappedTenants () {
      // Create a map of tenantId -> tenant
      const tenantMap = {}
      this.tenants.forEach(tenant => {
        tenantMap[tenant.id] = tenant
      })

      const emptyGuid = '00000000-0000-0000-0000-000000000000'
      const sections = this.endUserSections || []

      // Rows are driven by EndUsers (incl. MinimalUser guests with TenantId only).
      // EndUserTenantLogin is optional — missing mapping = Gastbestellung.
      return sections
        .flatMap(section => {
          const endUserId = section.endUserId
          // Wait until fromenduser was called for this EndUser (404 → empty list = Gast).
          if (!this.$store.getters['endusertenantlogins/hasFetchedByEndUserId'](endUserId)) {
            return []
          }

          const tenantLogins = this.$store.getters['endusertenantlogins/getUnwrappedByEndUserId'](endUserId) || []

          if (tenantLogins.length === 0) {
            return [{
              endUserId,
              tenantName: section.tenantName || (section.tenantId && tenantMap[section.tenantId]
                ? tenantMap[section.tenantId].name
                : `Unknown (${section.tenantId})`),
              hasTenantLogin: false,
              endUserTenantLoginId: null,
              emailConfirmed: false,
              lockoutEndDate: null,
              lastPasswordReset: null,
              created: null,
              updated: null
            }]
          }

          return tenantLogins.map(login => {
            const tenantId = login.tenantId ?? login.TenantId ?? section.tenantId
            const tenant = tenantMap[tenantId]

            // Check if tenantLoginId exists and is not null/undefined and not empty GUID
            // Try both camelCase and PascalCase property names
            const tenantLoginId = login.tenantLoginId || login.TenantLoginId
            const hasTenantLogin = !!(tenantLoginId && tenantLoginId !== emptyGuid)

            return {
              endUserId,
              tenantName: section.tenantName || (tenant ? tenant.name : `Unknown (${tenantId})`),
              hasTenantLogin,
              endUserTenantLoginId: login.id,
              emailConfirmed: login.emailConfirmed || login.EmailConfirmed || false,
              lockoutEndDate: login.lockoutEndDate || login.LockoutEndDate || null,
              lastPasswordReset: login.lastPasswordReset || login.LastPasswordReset || null,
              created: login.created,
              updated: login.updated
            }
          })
        })
        .sort((a, b) => a.tenantName.localeCompare(b.tenantName))
    }
  },
  methods: {
    getTenants() {
      this.$store.dispatch('tenants/getAll')
    },
    getTenantLogins(endUserId) {
      this.$store.dispatch('endusertenantlogins/getByEndUser', endUserId)
    },
    async triggerPasswordreset(endUserId, item) {
      if (!item.endUserTenantLoginId) return
      await this.$store.dispatch('endusertenantlogins/triggerPasswordreset', item.endUserTenantLoginId)
      await this.getTenantLogins(endUserId)
    },
    async triggerEmailConfirmation(endUserId, item) {
      if (!item.endUserTenantLoginId) return
      await this.$store.dispatch('endusertenantlogins/triggerEmailConfirmation', item.endUserTenantLoginId)
      await this.getTenantLogins(endUserId)
    },
    async triggerResetLockout(endUserId, item) {
      if (!item.endUserTenantLoginId) return
      await this.$store.dispatch('endusertenantlogins/triggerResetLockout', item.endUserTenantLoginId)
      await this.getTenantLogins(endUserId)
    },
    isActionLoading(endUserTenantLoginId, actionKey) {
      const is = this.$store.getters['endusertenantlogins/is']
      return is.sending && is.sending[actionKey] === true
    }
  },
  created() {
    this.getTenants()
    // EndUsers are loaded by the sections mixin; once available, fetch tenant logins per EndUser.
    this.$watch('endUserSections', sections => {
      (sections || []).forEach(s => this.getTenantLogins(s.endUserId))
    }, { immediate: true })
  }
}
</script>

<style lang="scss">
::v-deep .actions-column {
  white-space: nowrap;

  th,
  td {
    white-space: nowrap;
    padding-left: 0.5rem !important;
    padding-right: 0.5rem !important;
  }
}

.actions-cell {
  white-space: nowrap;
  width: 1% !important;
  padding-left: 0.5rem !important;
  padding-right: 0.5rem !important;
}

.actions-buttons {
  width: fit-content;
  margin: 0;
  flex-wrap: nowrap !important;

  .btn {
    white-space: nowrap !important;
    flex-shrink: 0;
  }
}
</style>
